import {safeOptional} from '@github-ui/swp-core/schemas/safe-optional'
import * as z from 'zod'
import {BasePayloadSchema} from './base-payload'
import {TopicSchema} from './topic'
import {ResourceSummarySchema} from './resource-summary'

/**
 * Schema for concept scheme info (id and localized label).
 */
export const SchemeInfoSchema = z.object({
  id: z.string(),
  preferred_label: z.string().nullable(),
})

/**
 * Schema for a filter group containing a scheme and its concepts.
 */
export const FilterGroupSchema = z.object({
  scheme: SchemeInfoSchema,
  concepts: z.array(TopicSchema),
})

/**
 * Schema for filters as an array of filter groups.
 * Each group contains a scheme (with id and localized label) and its concepts.
 */
export const FiltersSchema = z.array(FilterGroupSchema)

export type Filters = z.infer<typeof FiltersSchema>

/**
 * Payload schema for dynamic index pages with taxonomy-based filtering.
 * Used for index pages where resources are fetched dynamically
 * based on selected concepts from the taxonomy service.
 */
export const DynamicIndexPayloadSchema = z.lazy(() =>
  BasePayloadSchema.unwrap().extend({
    filters: FiltersSchema,
    resources: safeOptional(z.array(ResourceSummarySchema)),
    additionalProps: safeOptional(
      z.object({
        selectedFilters: safeOptional(z.record(z.string(), z.array(z.string()))),
        totalPages: z.number().optional(),
        sortOptions: safeOptional(
          z.array(
            z.object({
              label: z.string(),
              value: z.string(),
            }),
          ),
        ),
        currentSort: z.string().optional(),
      }),
    ),
  }),
)

export type DynamicIndexPayload = z.infer<typeof DynamicIndexPayloadSchema>

export function toDynamicIndexPayload(payload: unknown): DynamicIndexPayload {
  return DynamicIndexPayloadSchema.parse(payload)
}

export function isDynamicIndexPayload(payload: unknown): payload is DynamicIndexPayload {
  return DynamicIndexPayloadSchema.safeParse(payload).success
}
